/* =========================================================
   VK CHECKOUT CONTROLLER v3.0.0 — THIN CLIENT
   – Pricing/rules logic now lives SERVER-SIDE on vk-connect:
       POST https://api.vandaskitchen.co.uk/api/checkout-rules
       returns: zone, delivery fee, free threshold, lead time,
                blocked dates, minimums, volume discount, order types
   – This file is the THIN CLIENT: it does DOM work only —
     rendering, calendars, morph protection, positioning, the
     fetch interceptor. It asks the API for every decision.
   – Local detectOrderTypes is kept ONLY to choose which UI panel
     to draw instantly; the API is the source of truth for all
     pricing. If the API is unreachable, the UI still renders and
     the controller fails safe (no fee added, checkout still works).
   – Zone fee variant IDs (Shopify "Delivery Fee" product) — 2026-06
     5-zone restructure. MUST match checkoutRules/zones.ts fees and the
     planner's FEE_VARIANT_BY_ZONE in vk-connect:
       Zone 1 £12.50 → 57746003525964
       Zone 2 £16.95 → 56693291614540
       Zone 3 £21.95 → 56693291647308
       Zone 4 £27.50 → 57746003558732
       Zone 5 £35.00 → 57746011390284
       (old £31.95 variant 56693291680076 now dormant)
   – All DOM/morph/observer engineering carried over from v2.0.0
     unchanged.
   ========================================================= */
(function () {
  'use strict';
  if (window.__vkCheckoutBound) return;
  window.__vkCheckoutBound = true;

  /* ── API ────────────────────────────────────────────────────── */
  var API_BASE = 'https://api.vandaskitchen.co.uk';
  var RULES_ENDPOINT = API_BASE + '/api/checkout-rules';

  /* Zone number → Shopify Delivery Fee variant ID.
     The API returns the zone number; the browser still has to add
     the correct Shopify line item, so this map stays client-side. */
  var ZONE_VARIANT = {
    0: 57913091359052,  // £0.00   Zone 0 — Walking distance (free walk-delivery)
    1: 57746003525964,  // £12.50  Zone 1 — City core
    2: 56693291614540,  // £16.95  Zone 2 — Inner central
    3: 56693291647308,  // £21.95  Zone 3 — Mid London
    4: 57746003558732,  // £27.50  Zone 4 — Outer
    5: 57746011390284   // £35.00  Zone 5 — Far
  };
  /* All fee variant IDs (incl. the dormant £31.95) so the cart row stays
     hidden whichever one is present. */
  var FEE_VARIANT_IDS = [57913091359052, 57746003525964, 56693291614540, 56693291647308, 57746003558732, 57746011390284, 56693291680076].map(String);

  var CONFIG = {
    timeSlots: ['07:00 – 08:00','08:00 – 09:00','09:00 – 10:00','10:00 – 11:00','11:00 – 12:00','12:00 – 13:00','13:00 – 14:00','14:00 – 15:00','15:00 – 16:00','16:00 – 17:00','17:00 – 18:00'],
    pickupAddress: "Vanda's Kitchen, St Paul's, London EC4V 5EA"
  };

  var ICONS = {
    truck: '<svg class="vkc-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
    pin: '<svg class="vkc-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
    check: '<svg class="vkc-icon vkc-icon--check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#309B42" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
    lock: '<svg class="vkc-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>',
    arrow: '<svg class="vkc-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>'
  };

  /* STATE — `rules` holds the latest API response. */
  var STATE = { method: null, postcode: '', postcodeValid: null, zone: null, types: null, rules: null, ready: false, saving: false, rendered: false, positioned: false, rerendering: false };
  var BLOCKED_DATES = [];
  var _observer = null;
  var _cartCache = null;
  var _cartCacheTime = 0;
  var _rulesSeq = 0;

  function safeCartFetch() {
    if (_cartCache && Date.now() - _cartCacheTime < 1000) return Promise.resolve(_cartCache);
    return fetch('/cart.js').then(function(r) {
      if (!r.ok) { console.warn('[VK Checkout] Cart fetch HTTP', r.status); return _cartCache || null; }
      return r.json();
    }).then(function(cart) {
      if (cart) { _cartCache = cart; _cartCacheTime = Date.now(); }
      return cart;
    }).catch(function(e) { console.warn('[VK Checkout] Cart fetch error:', e.message); return _cartCache || null; });
  }

  /* ── API CALL — the decision engine ─────────────────────────────
     Sends the cart + postcode to /api/checkout-rules and stores the
     response in STATE.rules. Fails safe: on any error STATE.rules is
     left as a permissive fallback so the checkout still works. */
  function fetchRules(cart, postcode) {
    var seq = ++_rulesSeq;   /* only the newest request may write STATE.rules */
    var body = {
      postcode: postcode || null,
      cart: {
        items: (cart.items || []).map(function(it) {
          return {
            title: it.title, quantity: it.quantity,
            final_line_price: it.final_line_price,
            variant_title: it.variant_title || '',
            properties: it.properties || {}
          };
        }),
        items_subtotal_price: cart.items_subtotal_price
      }
    };
    return fetch(RULES_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function(r) {
      if (!r.ok) throw new Error('checkout-rules HTTP ' + r.status);
      return r.json();
    }).then(function(rules) {
      if (seq !== _rulesSeq) return STATE.rules;   /* stale response — a newer check superseded it */
      STATE.rules = rules;
      if (rules && rules.blocked_dates) BLOCKED_DATES = rules.blocked_dates;
      return rules;
    }).catch(function(e) {
      console.warn('[VK Checkout] checkout-rules failed, failing safe:', e.message);
      if (seq !== _rulesSeq) return STATE.rules;
      /* Fail-safe rules: no zone known, no fee added, no false blocks.
         Checkout still functions; fee can be settled manually if needed. */
      STATE.rules = STATE.rules || {
        zone: null,
        delivery: { fee_pence: 0, free: false, free_threshold_pence: 60000, gap_to_free_pence: null },
        lead_time: { min_days: 1, reason: null },
        blocked_dates: BLOCKED_DATES,
        minimums: { errors: [] },
        volume_discount: { applies: false },
        order_types: {},
        _fallback: true
      };
      return STATE.rules;
    });
  }

  /* ── BLOCKED DATES ──────────────────────────────────────────── */
  function isBlocked(isoDate) { return BLOCKED_DATES.indexOf(isoDate) > -1; }

  /* ── SIDEBAR POSITIONING ────────────────────────────────────── */
  function repositionController() {
    var ctrl = document.getElementById('vk-checkout-controller');
    if (!ctrl) return false;
    if (STATE.positioned && ctrl.closest('.cart__summary-inner')) return true;
    var inner = document.querySelector('.cart__summary-inner');
    if (inner) {
      var ctas = inner.querySelector('.cart__ctas');
      if (ctas) inner.insertBefore(ctrl, ctas); else inner.appendChild(ctrl);
      STATE.positioned = true; onPositioned(); return true;
    }
    return false;
  }
  function onPositioned() { document.body.classList.add('vkc-positioned'); hideThemeCheckout(); hideDeliveryFeeRow(); }
  function hideThemeCheckout() {
    if (!STATE.positioned) return;
    var ctas = document.querySelector('.cart__summary-inner .cart__ctas');
    if (ctas) ctas.style.display = 'none';
  }
  function hideDeliveryFeeRow() {
    var rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    rows.forEach(function(row) {
      var key = row.getAttribute('data-key') || '';
      var vid = key.split(':')[0];
      if (FEE_VARIANT_IDS.indexOf(vid) > -1) row.style.display = 'none';
    });
  }
  function setupMorphProtection() {
    var cs = document.querySelector('cart-items-component');
    if (!cs) return;
    if (_observer) _observer.disconnect();
    _observer = new MutationObserver(function() {
      if (STATE.saving) return;
      var ctrl = document.getElementById('vk-checkout-controller');
      if (!ctrl) return;
      if (!ctrl.closest('.cart__summary-inner')) {
        STATE.positioned = false; repositionController(); hideThemeCheckout(); hideDeliveryFeeRow();
      }
    });
    _observer.observe(cs, { childList: true, subtree: true });
  }

  /* ── LOCAL ORDER-TYPE DETECTION ──────────────────────────────────
     Kept LOCAL so the correct UI panel renders instantly without
     waiting on the network. The API also classifies order types and
     is authoritative for pricing — but the UI shell needs this now. */
  function detectOrderTypes(cart) {
    var t = { hasFreedomTray: false, hasTeamLunch: false, hasShopItems: false, hasQuiche: false, hasLongLead: false, isConnectedCateringPlanner: false, teamLunchDays: 0, teamLunchTotal: 0, teamLunchMeals: 0, shopTotal: 0, freedomTrayTotal: 0, totalValue: 0, freedomTrayDates: [], teamLunchDates: [] };
    (cart.items || []).forEach(function (it) {
      var title = (it.title || '').toLowerCase(), props = it.properties || {};
      var variantTitle = (it.variant_title || '').toLowerCase();
      var ot = (props._order_type || props['_order_type'] || '').toLowerCase();
      var line = it.final_line_price / 100;
      if (title.indexOf('delivery fee') > -1 || (props._delivery_fee === 'true')) return;
      if (ot.indexOf('connected catering planner') > -1) { t.isConnectedCateringPlanner = true; }
      if (ot.indexOf('team lunch') > -1 || ot.indexOf('weekly team') > -1) {
        t.hasTeamLunch = true; t.teamLunchTotal += line; t.teamLunchMeals += (it.quantity || 1);
        var dd = props._delivery_days || props['_delivery_days'] || '';
        if (dd) { var c = dd.split(',').length; if (c > t.teamLunchDays) t.teamLunchDays = c; }
        Object.keys(props).forEach(function(k) {
          if (k.charAt(0) === '_') return;
          var v = props[k];
          if (v && /\d{4}-\d{2}-\d{2}/.test(String(v)) && t.teamLunchDates.indexOf(v) === -1) t.teamLunchDates.push(v);
          if (/^deliver/i.test(k) && v && t.teamLunchDates.indexOf(String(v)) === -1) t.teamLunchDates.push(String(v));
        });
      } else if (title.indexOf('freedom tray') > -1 && ot === '') {
        t.hasFreedomTray = true; t.freedomTrayTotal += line;
        Object.keys(props).forEach(function(k) {
          if (k.charAt(0) === '_') return;
          var v = String(props[k] || '');
          if (/^deliver/i.test(k) && v && t.freedomTrayDates.indexOf(v) === -1) t.freedomTrayDates.push(v);
        });
      } else {
        t.hasShopItems = true; t.shopTotal += line;
        if (title.indexOf('quiche') > -1) t.hasQuiche = true;
        if (title.indexOf('meal pack') > -1 && (variantTitle.indexOf('20') > -1 || variantTitle.indexOf('40') > -1)) t.hasLongLead = true;
      }
    });
    t.totalValue = t.shopTotal + t.teamLunchTotal + t.freedomTrayTotal;
    return t;
  }

  function extractOutcode(pc) { var c = pc.replace(/\s+/g, '').toUpperCase(); var m = c.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)\s?\d[A-Z]{2}$/); if (m) return m[1]; m = c.match(/^([A-Z]{1,2}\d{1,2}[A-Z]?)$/); return m ? m[1] : null; }

  /* ── RULES-DERIVED HELPERS ──────────────────────────────────────
     These read STATE.rules (the API response) instead of computing
     anything locally. */
  function rulesZone() { return STATE.rules && STATE.rules.zone ? STATE.rules.zone : null; }
  function rulesDelivery() { return (STATE.rules && STATE.rules.delivery) || null; }
  function feeLabel() {
    var d = rulesDelivery();
    if (!d || rulesZone() == null) return 'Enter postcode';
    if (d.free) return 'FREE';
    return '£' + (d.fee_pence / 100).toFixed(2);
  }
  function minimumErrors() {
    return (STATE.rules && STATE.rules.minimums && STATE.rules.minimums.minimums ? STATE.rules.minimums.minimums.errors : null)
      || (STATE.rules && STATE.rules.minimums && STATE.rules.minimums.errors) || [];
  }
  function volumeDiscount() { return (STATE.rules && STATE.rules.volume_discount) || { applies: false }; }
  function leadMinDays() { return (STATE.rules && STATE.rules.lead_time && STATE.rules.lead_time.min_days) || 1; }
  /* The authoritative earliest delivery date, as a LOCAL midnight Date.
     Parsed from the API's YYYY-MM-DD by parts — never `new Date(iso)`,
     which parses as UTC and rolls back a day in BST. */
  function earliestFromRules(today, hr) {
    var iso = STATE.rules && STATE.rules.lead_time && STATE.rules.lead_time.earliest_delivery_date;
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      var p = iso.split('-');
      var d = new Date(parseInt(p[0],10), parseInt(p[1],10) - 1, parseInt(p[2],10));
      d.setHours(0,0,0,0);
      return d;
    }
    /* Fail-safe ONLY: the API is unreachable, so STATE.rules is the local
       fallback object, which carries no earliest_delivery_date. Mirror the
       server's standard-cart rule (1 working day, 14:00 cutoff); the
       caller's weekend skip handles Sat/Sun. Deliberately conservative —
       quiche leads are unknown here, and the server re-checks on submit. */
    var f = new Date(today);
    f.setDate(f.getDate() + (hr >= 14 ? 2 : 1));
    f.setHours(0,0,0,0);
    return f;
  }
  function leadReason() { return STATE.rules && STATE.rules.lead_time ? STATE.rules.lead_time.reason : null; }

  function fmtISO(d) { return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); }

  /* ── HTML ───────────────────────────────────────────────────── */
  function htmlPostcode() { return '<div class="vkc-field"><label class="vkc-label">Delivery postcode</label><div class="vkc-postcode-row"><input type="text" id="vkc-postcode" class="vkc-input" placeholder="e.g. EC2A 4BX" maxlength="8" autocomplete="postal-code"><button type="button" id="vkc-pc-btn" class="vkc-btn--sm">Check</button></div><div id="vkc-pc-msg" class="vkc-pc-msg"></div></div>'; }
  function htmlDates(group) {
    var g = group || 'default';
    var h = '<div class="vkc-calendar" data-group="' + g + '"><div class="vkc-cal-header"><button type="button" class="vkc-cal-nav" data-dir="-1" data-group="' + g + '">‹</button><span class="vkc-cal-month" data-group="' + g + '"></span><button type="button" class="vkc-cal-nav" data-dir="1" data-group="' + g + '">›</button></div><div class="vkc-cal-days">';
    ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].forEach(function(d) { h += '<span class="vkc-cal-dayname">' + d + '</span>'; });
    return h + '</div><div class="vkc-cal-grid" data-group="' + g + '"></div></div>';
  }
  var calState = {};
  function initCals() {
    document.querySelectorAll('.vkc-calendar').forEach(function(c) { var g = c.dataset.group || 'default'; var n = new Date(); calState[g] = { year: n.getFullYear(), month: n.getMonth() }; renderCal(g); });
    document.querySelectorAll('.vkc-cal-nav').forEach(function(b) {
      b.addEventListener('click', function() {
        var g = this.dataset.group, d = parseInt(this.dataset.dir), s = calState[g]; s.month += d;
        if (s.month > 11) { s.month = 0; s.year++; } if (s.month < 0) { s.month = 11; s.year--; }
        var n = new Date(); if (s.year < n.getFullYear() || (s.year === n.getFullYear() && s.month < n.getMonth())) { s.year = n.getFullYear(); s.month = n.getMonth(); }
        renderCal(g);
      });
    });
  }
  function renderCal(group) {
    var s = calState[group], grid = document.querySelector('.vkc-cal-grid[data-group="' + group + '"]'), lbl = document.querySelector('.vkc-cal-month[data-group="' + group + '"]');
    if (!grid || !lbl) return;
    lbl.textContent = ['January','February','March','April','May','June','July','August','September','October','November','December'][s.month] + ' ' + s.year;
    var now = new Date(), today = new Date(now.getFullYear(), now.getMonth(), now.getDate()), hr = now.getHours();
    /* Earliest delivery date comes STRAIGHT from the API. vk-connect's
       earliestDeliveryDate() already applies, in one place: the 14:00
       Europe/London cutoff, working-day math (Mon-Fri), the 2-working-day
       quiche / meal-pack lead, and BLOCKED_DATES.

       DO NOT re-derive this from min_days. min_days is
       workingDaysBetween(today, earliest) — measured from TODAY — so the
       cutoff push is ALREADY inside it. The previous line added
       "+1 if after 14:00" on top of that, counting the cutoff TWICE, and
       every order placed after 2pm silently lost a day: on Tue 14 Jul at
       17:00 the calendar offered Friday 17th when the server said
       Thursday 16th. Before 14:00 the +1 never fired, which is why it went
       unnoticed — it only ever broke in the afternoon. */
    var ear = earliestFromRules(today, hr);
    while (ear.getDay() === 0 || ear.getDay() === 6) ear.setDate(ear.getDate() + 1); ear.setHours(0,0,0,0);
    var fd = new Date(s.year, s.month, 1).getDay(), off = (fd === 0) ? 6 : fd - 1, dim = new Date(s.year, s.month + 1, 0).getDate();
    var prev = null, es = grid.querySelector('.vkc-cal-cell.is-selected'); if (es) prev = es.dataset.date;
    var h = ''; for (var i = 0; i < off; i++) h += '<span class="vkc-cal-cell vkc-cal-cell--empty"></span>';
    for (var d = 1; d <= dim; d++) {
      var dt = new Date(s.year, s.month, d); dt.setHours(0,0,0,0); var dw = dt.getDay();
      var iso = fmtISO(dt);
      var dis = dt < ear || dw === 0 || dw === 6 || isBlocked(iso);
      var cl = 'vkc-cal-cell'; if (dis) cl += ' vkc-cal-cell--disabled';
      if (isBlocked(iso) && dt >= ear && dw !== 0 && dw !== 6) cl += ' vkc-cal-cell--blocked';
      if (dt.getTime() === today.getTime()) cl += ' vkc-cal-cell--today';
      if (prev === iso && !dis) cl += ' is-selected';
      h += dis ? '<span class="' + cl + '" title="' + (isBlocked(iso) ? 'Unavailable' : '') + '">' + d + '</span>' : '<button type="button" class="' + cl + '" data-date="' + iso + '" data-group="' + group + '">' + d + '</button>';
    }
    grid.innerHTML = h;
    grid.querySelectorAll('button.vkc-cal-cell').forEach(function(b) { b.addEventListener('click', function() { grid.querySelectorAll('.vkc-cal-cell').forEach(function(c) { c.classList.remove('is-selected'); }); this.classList.add('is-selected'); gate(); }); });
  }
  function htmlTime(id) { var h = '<select id="' + (id || 'vkc-time') + '" class="vkc-select"><option value="">Select a time slot</option>'; CONFIG.timeSlots.forEach(function(s) { h += '<option value="' + s + '">' + s + '</option>'; }); return h + '</select>'; }

  /* Zone fee box — reads STATE.rules. */
  function htmlFee() {
    var zone = rulesZone();
    var d = rulesDelivery();
    if (zone == null || !d) {
      return '<div class="vkc-fee">' +
        '<div class="vkc-fee__top"><span class="vkc-fee__label">' + ICONS.truck + ' Delivery fee</span><span class="vkc-fee__val">—</span></div>' +
        '<div class="vkc-fee__note">' + ICONS.pin + ' Enter your postcode above to see your delivery fee.</div>' +
        '</div>';
    }
    var zoneName = (STATE.rules.zone && STATE.rules.zone.label) ? STATE.rules.zone.label : ('Zone ' + zone);
    var free = d.free;
    var h = '<div class="vkc-fee">';
    h += '<div class="vkc-fee__top">';
    h += '<span class="vkc-fee__label">' + ICONS.truck + ' Delivery fee</span>';
    h += '<span class="vkc-fee__val' + (free ? ' vkc-fee--free' : '') + '">' + feeLabel() + '</span>';
    h += '</div>';
    h += '<div class="vkc-fee__zone">' + zoneName + '</div>';
    var gapPence = d.gap_to_free_pence;
    if (!free && gapPence && gapPence > 0) {
      var thr = (d.free_threshold_pence / 100).toFixed(0);
      /* NOT "any zone": the free-delivery bar is per-zone (£250 in the City,
         Holborn and Bankside; £600 elsewhere). The zone line directly above
         already states this zone's threshold, so don't restate it wrongly. */
      h += '<div class="vkc-fee__note">' + ICONS.truck + ' Add £' + (gapPence / 100).toFixed(2) + ' more and your delivery is free.</div>';
    } else if (free) {
      h += '<div class="vkc-fee__note vkc-fee__note--ok">' + ICONS.check + ' Your order qualifies for free delivery.</div>';
    }
    return h + '</div>';
  }
  function htmlBtn() { return '<div class="vkc-checkout-wrap"><button type="button" id="vkc-checkout-btn" class="vkc-checkout-btn vkc-btn--disabled" disabled>' + ICONS.lock + ' <span class="vkc-checkout-btn__text">Please select postcode, date, time</span></button><div class="vkc-checkout-secure">' + ICONS.lock + ' Secure checkout</div><div class="vkc-trust-strip"><span class="vkc-trust-item">🚫🥜 Prepared in our 100% nut-free kitchen</span><span class="vkc-trust-item">🍖 100% Halal certified</span><span class="vkc-trust-item">💙 Blue Light Card accepted</span></div></div>'; }

  function htmlVolumeDiscount() {
    var vd = volumeDiscount();
    if (!vd || !vd.applies) {
      return '<div class="vkc-volume"></div>';
    }
    var t = STATE.types || {};
    var savings = vd.label && t.teamLunchTotal ? '' : '';
    var h = '<div class="vkc-volume"><span class="vkc-volume__inline">🎉 <strong>' + (vd.label || 'Discount') + '</strong>';
    if (vd.meals) h += ' — ' + vd.meals + ' meals';
    h += '</span></div>';
    return h;
  }

  /* ── RENDER ─────────────────────────────────────────────────── */
  function render(cart, force) {
    var el = document.getElementById('vk-checkout-controller'); if (!el || STATE.saving) return;
    var t = detectOrderTypes(cart); STATE.types = t;
    if (STATE.rendered && !force) { updFee(); gate(); return; }
    var html = '', errs = minimumErrors();
    if (errs.length) { html += '<div class="vkc-warnings">'; errs.forEach(function(e) { html += '<div class="vkc-warning">⚠️ ' + e + '</div>'; }); html += '</div>'; }
    var lr = leadReason();
    if (lr) {
      html += '<div class="vkc-warning" style="background:#fff8e1;border-color:#f0c040;color:#7a5900;">⏰ ' + lr + '. Please allow extra preparation time — the earliest available delivery date has been adjusted.</div>';
    }
    if (t.hasFreedomTray && !t.hasShopItems && !t.hasTeamLunch) html += panelPickup();
    else if (t.hasFreedomTray && (t.hasShopItems || t.hasTeamLunch)) html += panelMixed(t);
    else if (t.hasTeamLunch && !t.hasShopItems) html += panelTeam(t);
    else if (t.hasShopItems || t.hasTeamLunch) html += panelStd(t);
    else html += '<div class="vkc-empty">Add items to your basket to continue</div>';
    html += htmlBtn(); el.innerHTML = html; bindAll(t); gate(); STATE.rendered = true;
  }
  function updFee() { var f = document.querySelector('.vkc-fee'); if (f) f.outerHTML = htmlFee(); var v = document.querySelector('.vkc-volume'); if (v) v.outerHTML = htmlVolumeDiscount(); updWarnings(); updCals(); }
  /* Blocked dates and the earliest-delivery date arrive with the rules,
     AFTER the calendars first render — repaint them whenever the rules
     change so a blocked day can't stay selectable (2026-07-23: the API
     blocked 24 Jul but the already-rendered calendar happily took the
     click). renderCal drops a selection that has become disabled, so
     gate() re-blocks checkout too. */
  function updCals() { try { Object.keys(calState).forEach(function(g) { renderCal(g); }); } catch (e) {} }
  /* Zone minimums arrive only after the postcode check, so the warnings
     banner painted at first render can be wrong — re-render it whenever
     the rules change (called from updFee). */
  function updWarnings() {
    var t = STATE.types; if (!t) return;
    var errs = minimumErrors();
    var show = errs.length > 0;
    var html = '';
    if (show) { html = '<div class="vkc-warnings">'; errs.forEach(function(e) { html += '<div class="vkc-warning">⚠️ ' + e + '</div>'; }); html += '</div>'; }
    var w = document.querySelector('.vkc-warnings');
    if (w) { if (show) { w.outerHTML = html; } else { w.parentNode.removeChild(w); } }
    else if (show) { var el = document.getElementById('vk-checkout-controller'); if (el) el.insertAdjacentHTML('afterbegin', html); }
  }

  function panelPickup() { STATE.method = 'pickup'; return '<div class="vkc-panel"><div class="vkc-header">' + ICONS.pin + '<span class="vkc-header__text">Collection from St Paul\'s</span></div><div class="vkc-address">' + CONFIG.pickupAddress + '</div><div class="vkc-field"><label class="vkc-label">Collection date</label>' + htmlDates(null) + '</div><div class="vkc-field"><label class="vkc-label">Collection time</label>' + htmlTime('vkc-time') + '</div><div class="vkc-pickup-note">' + ICONS.check + ' No delivery fee for collection</div></div>'; }
  function panelTeam(t) {
    STATE.method = 'delivery';
    var vd = volumeDiscount();
    var h = '<div class="vkc-panel"><div class="vkc-header">' + ICONS.truck + '<span class="vkc-header__text">Team Lunch Delivery</span>';
    if (vd && vd.applies) {
      h += '<span class="vkc-volume-line">🎉 ' + (vd.label || 'Discount') + (vd.meals ? ' · ' + vd.meals + ' meals' : '') + '</span>';
    }
    h += '</div>';
    h += '<div class="vkc-free-delivery">🚚 FREE delivery included in your meal price</div>';
    h += htmlPostcode() + '<div class="vkc-field"><label class="vkc-label">Preferred time (all days)</label>' + htmlTime('vkc-time') + '</div>';
    return h + '</div>';
  }
  function panelStd(t) {
    STATE.method = 'delivery';
    var h = '<div class="vkc-panel">';
    var hasMultipleTypes = t.hasTeamLunch && t.hasShopItems;
    if (hasMultipleTypes) {
      h += '<div class="vkc-order-strip">';
      h += '<div class="vkc-strip-title">Your Order</div>';
      var vd = volumeDiscount();
      h += '<div class="vkc-strip-row"><span class="vkc-strip-label">🍱 Team Lunch</span><span class="vkc-strip-val">' + t.teamLunchMeals + ' meal' + (t.teamLunchMeals > 1 ? 's' : '') + ' · £' + t.teamLunchTotal.toFixed(2) + '</span></div>';
      if (vd && vd.applies) {
        h += '<div class="vkc-strip-row"><span class="vkc-strip-label">Discount</span><span class="vkc-strip-val vkc-strip-green">🎉 ' + (vd.label || 'Discount') + '</span></div>';
      }
      h += '<div class="vkc-strip-divider"></div>';
      h += '<div class="vkc-strip-row"><span class="vkc-strip-label">🍽️ Catering</span><span class="vkc-strip-val">£' + t.shopTotal.toFixed(2) + '</span></div>';
      h += '<div class="vkc-strip-row"><span class="vkc-strip-label">' + ICONS.truck + ' Delivery</span><span class="vkc-strip-val">' + feeLabel() + '</span></div>';
      h += '</div>';
    }
    h += '<div class="vkc-toggle"><button type="button" class="vkc-toggle__btn is-active" data-method="delivery">' + ICONS.truck + ' <span>Delivery</span></button><button type="button" class="vkc-toggle__btn" data-method="pickup">' + ICONS.pin + ' <span>Collection</span></button></div>';
    h += '<div id="vkc-sec-delivery" class="vkc-section">' + htmlPostcode() + '<div class="vkc-field"><label class="vkc-label">Delivery date</label>' + htmlDates(null) + '</div><div class="vkc-field"><label class="vkc-label">Preferred time</label>' + htmlTime('vkc-time') + '</div><div class="vkc-field"><label class="vkc-label">Delivery note <span style="font-weight:400;color:#888">(optional)</span></label><textarea id="vkc-note" class="vkc-input vkc-textarea" rows="2" placeholder="e.g. Ring bell at reception, leave with security"></textarea></div>' + htmlFee() + '</div>';
    h += '<div id="vkc-sec-pickup" class="vkc-section" style="display:none"><div class="vkc-address">' + CONFIG.pickupAddress + '</div><div class="vkc-field"><label class="vkc-label">Collection date</label>' + htmlDates('pu') + '</div><div class="vkc-field"><label class="vkc-label">Collection time</label>' + htmlTime('vkc-time-pu') + '</div><div class="vkc-pickup-note">' + ICONS.check + ' No delivery fee for collection</div></div>';
    return h + '</div>';
  }
  function panelMixed(t) {
    STATE.method = 'mixed';
    var h = '<div class="vkc-mixed-banner"><strong>📦 Mixed order</strong> — Freedom Tray: <strong>collection only</strong>. Other items can be delivered.</div>';
    h += '<div class="vkc-sub-panel"><div class="vkc-sub-title">' + ICONS.pin + ' Freedom Tray — Collection</div><div class="vkc-address">' + CONFIG.pickupAddress + '</div><div class="vkc-field"><label class="vkc-label">Collection date</label>' + htmlDates('ft') + '</div><div class="vkc-field"><label class="vkc-label">Collection time</label>' + htmlTime('vkc-time-ft') + '</div></div>';
    if (t.hasShopItems || t.hasTeamLunch) h += '<div class="vkc-sub-panel"><div class="vkc-sub-title">' + ICONS.truck + ' Other items — Delivery</div>' + htmlPostcode() + '<div class="vkc-field"><label class="vkc-label">Delivery date</label>' + htmlDates('del') + '</div><div class="vkc-field"><label class="vkc-label">Delivery time</label>' + htmlTime('vkc-time-del') + '</div>' + htmlFee() + '</div>';
    return h;
  }

  /* ── BINDING ────────────────────────────────────────────────── */
  function bindAll(t) {
    initCals();
    document.querySelectorAll('.vkc-toggle__btn').forEach(function(b) { b.addEventListener('click', function() { var m = this.dataset.method; document.querySelectorAll('.vkc-toggle__btn').forEach(function(x) { x.classList.remove('is-active'); }); this.classList.add('is-active'); STATE.method = m; var ds = document.getElementById('vkc-sec-delivery'), ps = document.getElementById('vkc-sec-pickup'); if (m === 'delivery') { if (ds) ds.style.display = ''; if (ps) ps.style.display = 'none'; } else { if (ds) ds.style.display = 'none'; if (ps) ps.style.display = ''; } gate(); }); });
    var pb = document.getElementById('vkc-pc-btn'), pi = document.getElementById('vkc-postcode'), pd = null;
    if (pb) pb.addEventListener('click', function() { chkPC(false); });
    if (pi) { pi.addEventListener('keyup', function(e) { if (e.key === 'Enter') chkPC(false); }); pi.addEventListener('input', function() { if (pd) clearTimeout(pd); var v = this.value.trim(); if (v.length >= 2) { pd = setTimeout(function() { chkPC(true); }, 500); } else { STATE.postcodeValid = null; STATE.zone = null; var m = document.getElementById('vkc-pc-msg'); if (m) m.innerHTML = ''; gate(); } gate(); }); }
    document.querySelectorAll('.vkc-select').forEach(function(s) { s.addEventListener('change', function() { gate(); }); });
    var vb = document.getElementById('vkc-checkout-btn'); if (vb) vb.addEventListener('click', function() { if (STATE.ready) doCheckout(); });
  }

  /* Postcode check — now an API round-trip to /api/checkout-rules. */
  function chkPC(silent) {
    var inp = document.getElementById('vkc-postcode'), msg = document.getElementById('vkc-pc-msg'); if (!inp || !msg) return;
    var val = inp.value.trim();
    if (!val) { if (!silent) msg.innerHTML = '<span class="vkc-pc--err">Please enter your postcode</span>'; STATE.postcodeValid = false; STATE.zone = null; updFee(); gate(); return; }
    var oc = extractOutcode(val); if (!oc && silent) { STATE.postcodeValid = null; STATE.zone = null; msg.innerHTML = ''; updFee(); gate(); return; }
    if (!silent) msg.innerHTML = '<span class="vkc-pc--checking">Checking…</span>';
    var tok = (chkPC._tok = (chkPC._tok || 0) + 1);   /* only the latest check may paint */
    safeCartFetch().then(function(cart) {
      if (!cart) return;
      return fetchRules(cart, val).then(function(rules) {
        if (tok !== chkPC._tok) return;   /* stale — user typed again or re-checked */
        var zone = rules && rules.zone ? rules.zone : null;
        if (zone) {
          STATE.postcodeValid = true; STATE.postcode = val; STATE.zone = zone.number;
          inp.classList.add('vkc-input--valid'); inp.classList.remove('vkc-input--invalid');
          var d = rules.delivery || {};
          var thr = d.free_threshold_pence ? (d.free_threshold_pence/100).toFixed(0) : '600';
          var feeTxt = d.free ? 'FREE delivery' : ('£' + (d.fee_pence/100).toFixed(2) + ' delivery');
          var minTxt = (rules.minimums && rules.minimums.shop_min_order_pence) ? ' Minimum order £' + Math.round(rules.minimums.shop_min_order_pence / 100) + '.' : '';
          msg.innerHTML = '<span class="vkc-pc--ok">' + ICONS.check + ' We deliver to ' + (extractOutcode(val) || val) + ' — ' + (zone.label || ('Zone ' + zone.number)) + ', ' + feeTxt + ' (free over £' + thr + ').' + minTxt + '</span>';
        } else {
          STATE.postcodeValid = false; STATE.zone = null;
          inp.classList.remove('vkc-input--valid'); inp.classList.add('vkc-input--invalid');
          msg.innerHTML = '<span class="vkc-pc--err">❌ Sorry, ' + (extractOutcode(val) || val) + ' is outside our delivery area. <a href="/pages/corporate-catering-london" class="vkc-link">Enquire about bespoke delivery</a></span>';
        }
        updFee();
        gate();
      });
    });
  }

  /* ── GATING ─────────────────────────────────────────────────── */
  function gate() {
    var vb = document.getElementById('vkc-checkout-btn');
    var rdy = true, t = STATE.types; if (!t) { if (vb) vb.disabled = true; return; }
    if (minimumErrors().length > 0) rdy = false;
    var m = STATE.method;
    if (m === 'delivery' || m === null) {
      if (STATE.postcodeValid !== true && document.getElementById('vkc-postcode')) rdy = false;
      if (!document.querySelector('.vkc-cal-cell.is-selected') && (!t.hasTeamLunch || t.hasShopItems)) rdy = false;
      var te = document.getElementById('vkc-time'); if (te && !te.value) rdy = false;
    }
    if (m === 'pickup') { var ps = document.getElementById('vkc-sec-pickup') || document.querySelector('.vkc-panel'); if (ps && !ps.querySelector('.vkc-cal-cell.is-selected')) rdy = false; var pt = document.getElementById('vkc-time-pu') || document.getElementById('vkc-time'); if (pt && !pt.value) rdy = false; }
    if (m === 'mixed') { var fc = document.querySelector('.vkc-cal-grid[data-group="ft"]'); if (fc && !fc.querySelector('.is-selected')) rdy = false; var ft = document.getElementById('vkc-time-ft'); if (ft && !ft.value) rdy = false; if (STATE.postcodeValid !== true) rdy = false; var dc = document.querySelector('.vkc-cal-grid[data-group="del"]'); if (dc && !dc.querySelector('.is-selected')) rdy = false; var dt = document.getElementById('vkc-time-del'); if (dt && !dt.value) rdy = false; }
    STATE.ready = rdy;
    hideThemeCheckout();
    if (vb) {
      vb.disabled = !rdy; var ts = vb.querySelector('.vkc-checkout-btn__text') || vb;
      if (rdy) { ts.textContent = 'Proceed to checkout'; vb.classList.add('vkc-ready'); vb.classList.remove('vkc-btn--disabled'); var ic = vb.querySelector('.vkc-icon'); if (ic) ic.outerHTML = ICONS.arrow; }
      else {
        var ms = [];
        {
          if (m === 'delivery' || m === 'mixed') { if (STATE.postcodeValid !== true && document.getElementById('vkc-postcode')) ms.push('postcode'); }
          if (m !== 'mixed') { if (!document.querySelector('.vkc-cal-cell.is-selected') && (!t.hasTeamLunch || t.hasShopItems)) ms.push('date'); var tc = document.getElementById('vkc-time') || document.getElementById('vkc-time-pu'); if (tc && !tc.value) ms.push('time'); }
        }
        var minErrs = minimumErrors();
        ts.textContent = ms.length > 0 ? 'Please select ' + ms.join(', ') : (minErrs.length > 0 ? minErrs[0] : 'Complete details above');
        vb.classList.remove('vkc-ready'); vb.classList.add('vkc-btn--disabled'); var ic2 = vb.querySelector('.vkc-icon'); if (ic2) ic2.outerHTML = ICONS.lock;
      }
    }
  }

  function doCheckout() {
    if (!STATE.ready) return; STATE.saving = true;
    var vb = document.getElementById('vkc-checkout-btn');
    if (vb) { vb.disabled = true; var ts = vb.querySelector('.vkc-checkout-btn__text'); if (ts) ts.textContent = 'Processing…'; vb.classList.add('vkc-btn--loading'); }
    saveAttrs().then(function() {
      var vd = volumeDiscount();
      if (vd && vd.applies && vd.code) {
        window.location.href = '/discount/' + encodeURIComponent(vd.code) + '?redirect=/checkout';
        return;
      }
      window.location.href = '/checkout';
    }).catch(function() { window.location.href = '/checkout'; });
  }

  /* ── FEE LINE ITEMS ─────────────────────────────────────────── */
  function getVid(zoneNum) { return ZONE_VARIANT[zoneNum] || null; }
  function findFee(cart) { for (var i = 0; i < cart.items.length; i++) { var it = cart.items[i]; if ((it.title||'').toLowerCase().indexOf('delivery fee') > -1 || (it.properties && it.properties._delivery_fee === 'true')) return { item: it }; } return null; }
  function rmFee(cart) { var ex = findFee(cart); if (!ex) return Promise.resolve(cart); var u = {}; u[ex.item.key] = 0; window.__vkInternalCartWrite = true; return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: u }) }).then(function(r) { return r.json(); }).finally(function() { window.__vkInternalCartWrite = false; }); }
  function addFee(zoneNum, cart) {
    if (zoneNum == null) return rmFee(cart);
    var vid = getVid(zoneNum); if (!vid) return Promise.resolve(cart);
    return rmFee(cart).then(function() {
      window.__vkInternalCartWrite = true;
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: { '_delivery_fee': 'true', '_auto_added': 'true', '_zone': String(zoneNum) } }] }) }).then(function(r) { return r.json(); }).finally(function() { window.__vkInternalCartWrite = false; });
    });
  }

  /* ── SAVE ───────────────────────────────────────────────────── */
  function saveAttrs() {
    var a = {}, t = STATE.types;
    var d = rulesDelivery() || {};
    var zone = rulesZone();

    {
      a['Delivery Method'] = STATE.method === 'pickup' ? 'Collection' : 'Delivery';
      if (t.freedomTrayDates.length > 0) a['Freedom Tray Delivery Dates'] = t.freedomTrayDates.join(', ');
      if (t.teamLunchDates.length > 0) a['Team Lunch Delivery Dates'] = t.teamLunchDates.join(', ');

      if (STATE.method === 'delivery') {
        a['Delivery Postcode'] = STATE.postcode;
        a['Delivery Fee'] = feeLabel();
        if (zone && STATE.rules.zone) a['Delivery Zone'] = STATE.rules.zone.label || ('Zone ' + zone);
        var sd = document.querySelector('.vkc-cal-cell.is-selected'); if (sd) a['Delivery Date'] = sd.dataset.date;
        if (t.hasTeamLunch && !sd && t.teamLunchDates.length > 0) a['Delivery Date'] = t.teamLunchDates.join(', ');
        else if (t.hasTeamLunch && !sd) a['Delivery Date'] = 'Per team lunch schedule (see line items)';
        var te = document.getElementById('vkc-time'); if (te && te.value) a['Delivery Time'] = te.value;
        var ne = document.getElementById('vkc-note'); if (ne && ne.value.trim()) a['Delivery Note'] = ne.value.trim();
      }
      if (STATE.method === 'pickup') {
        var pg = document.getElementById('vkc-sec-pickup') || document.querySelector('.vkc-panel');
        var pdsel = pg ? pg.querySelector('.vkc-cal-cell.is-selected') : null; if (pdsel) a['Collection Date'] = pdsel.dataset.date;
        var pt = document.getElementById('vkc-time-pu') || document.getElementById('vkc-time'); if (pt && pt.value) a['Collection Time'] = pt.value;
        a['Collection Address'] = CONFIG.pickupAddress; a['Delivery Fee'] = 'FREE (collection)';
      }
      if (STATE.method === 'mixed') {
        a['Delivery Method'] = 'Mixed (Collection + Delivery)';
        var fd = document.querySelector('.vkc-cal-grid[data-group="ft"] .vkc-cal-cell.is-selected'); if (fd) a['Freedom Tray Collection Date'] = fd.dataset.date;
        var ft2 = document.getElementById('vkc-time-ft'); if (ft2 && ft2.value) a['Freedom Tray Collection Time'] = ft2.value;
        a['Freedom Tray Collection Address'] = CONFIG.pickupAddress; a['Delivery Postcode'] = STATE.postcode;
        var dd2 = document.querySelector('.vkc-cal-grid[data-group="del"] .vkc-cal-cell.is-selected'); if (dd2) a['Other Items Delivery Date'] = dd2.dataset.date;
        var dt2 = document.getElementById('vkc-time-del'); if (dt2 && dt2.value) a['Other Items Delivery Time'] = dt2.value;
        a['Delivery Fee'] = feeLabel();
        if (zone && STATE.rules.zone) a['Delivery Zone'] = STATE.rules.zone.label || ('Zone ' + zone);
      }
    }

    var vd = volumeDiscount();
    if (vd && vd.applies) {
      if (vd.meals) a['Team Lunch Meals'] = String(vd.meals);
      a['Volume Discount'] = (vd.label || 'Discount') + (vd.meals ? ' (' + vd.meals + ' meals)' : '');
    }

    /* 2026-10-07: add first-party attribution (source + optional 'how did you find us') to the same attribute bundle. */
    try { if (window.vkAttribution) { var vkx = window.vkAttribution(); for (var vkk in vkx) { if (vkx[vkk]) a[vkk] = vkx[vkk]; } } } catch (e) {}

    return safeCartFetch().then(function(cart) {
      if (!cart) return Promise.resolve();
      /* Add the zone fee variant unless free / collection. */
      var addZone = null;
      if (STATE.method === 'delivery' || STATE.method === 'mixed') {
        if (zone != null && d && !d.free) {
          /* Attach the zone's delivery-fee variant as a cart line item.
             ZONE_VARIANT is keyed by zone NUMBER. Zone 0 is the £0
             walk-delivery line; paid zones 1-5 carry their fee. */
          if (zone.number === 0 || d.fee_pence > 0) addZone = zone.number;
        }
      }
      return (addZone != null ? addFee(addZone, cart) : rmFee(cart)).then(function() {
        window.__vkInternalCartWrite = true; return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: a }) }).then(function(r) { return r.json(); }).finally(function() { window.__vkInternalCartWrite = false; });
      });
    });
  }

  function interceptCheckout() {
    var form = document.getElementById('cart-form');
    if (form && !form.__vkcIntercepted) {
      form.addEventListener('submit', function(e) { e.preventDefault(); e.stopPropagation(); if (STATE.ready) doCheckout(); });
      form.__vkcIntercepted = true;
    }
    hideThemeCheckout();
  }

  function handleCartUpdated() {
    if (STATE.saving) { console.log('[VK Checkout] Recovery skipped (saving)'); return; }
    if (STATE.rerendering) { console.log('[VK Checkout] Recovery deferred (already rerendering)'); return; }
    STATE.rerendering = true;
    _cartCache = null;
    console.log('[VK Checkout] handleCartUpdated fired');

    safeCartFetch().then(function(cart) {
      if (!cart) { STATE.rerendering = false; return; }
      if (cart.item_count === 0 || (cart.items.every(function(it) { return (it.title||'').toLowerCase().indexOf('delivery fee') > -1 || (it.properties && it.properties._delivery_fee === 'true'); }))) {
        rmFee(cart).then(function() { STATE.rerendering = false; STATE.rendered = false; var el = document.getElementById('vk-checkout-controller'); if (el) el.innerHTML = ''; });
        return;
      }
      /* Refresh rules for the new cart (postcode carried if known). */
      fetchRules(cart, STATE.postcode || null).then(function() {
        var el = document.getElementById('vk-checkout-controller');
        if (!el) { el = document.createElement('div'); el.id = 'vk-checkout-controller'; document.body.appendChild(el); }
        var attempts = 0;
        function positionAndRender() {
          STATE.positioned = false;
          var placed = repositionController();
          if (!placed && attempts < 30) { attempts++; setTimeout(positionAndRender, 80); return; }
          el.style.display = '';
          STATE.rendered = false;
          render(cart, true);
          console.log('[VK Checkout] Full re-render complete after', attempts, 'positioning attempts');
          interceptCheckout(); hideDeliveryFeeRow();
          STATE.rerendering = false;
          setupMorphProtection();
        }
        positionAndRender();
      });
    }).catch(function(e) { console.error('[VK Checkout] Recovery error:', e); STATE.rerendering = false; });
  }

  /* ── INIT ───────────────────────────────────────────────────── */
  function init() {
    console.log('[VK Checkout] v3.0.0 (thin client) init starting');
    var el = document.getElementById('vk-checkout-controller');
    if (!el) { el = document.createElement('div'); el.id = 'vk-checkout-controller'; document.body.appendChild(el); }

    if (!repositionController()) {
      var rc = 0, ri = setInterval(function() { rc++; if (repositionController() || rc > 40) { clearInterval(ri); } }, 200);
    }
    setupMorphProtection();

    safeCartFetch().then(function(cart) {
      if (!cart) { console.warn('[VK Checkout] No cart data'); return; }
      console.log('[VK Checkout] Cart loaded:', cart.item_count, 'items');
      if (cart.item_count > 0) {
        /* First rules call — no postcode yet; gets lead time, minimums,
           order types, blocked dates. Zone/fee fill in after postcode. */
        fetchRules(cart, null).then(function() {
          render(cart, true); interceptCheckout(); hideDeliveryFeeRow();
          console.log('[VK Checkout] Initial render complete');
        });
      }
    }).catch(function(e) { console.error('[VK Checkout] Init failed:', e); });

    setInterval(function() {
      if (STATE.saving || STATE.rerendering) return;
      var ctrl = document.getElementById('vk-checkout-controller');
      if (!ctrl) return;
      var inSidebar = ctrl.closest('.cart__summary-inner');
      var hasContent = ctrl.querySelector('.vkc-panel, .vkc-mixed-banner, .vkc-empty, .vkc-warnings, .vkc-checkout-btn');
      var isHidden = ctrl.style.display === 'none';
      if (!inSidebar || !hasContent || isHidden) {
        console.log('[VK Checkout] Safety net: controller missing/empty/hidden — forcing recovery');
        handleCartUpdated();
      }
    }, 1500);
  }

  var CART_WRITE_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/i;
  var _recoveryTimer = null;
  var _detached = false;

  function scheduleRecovery() {
    if (_recoveryTimer) clearTimeout(_recoveryTimer);
    if (STATE.saving) return;
    _recoveryTimer = setTimeout(function() {
      _recoveryTimer = null; _detached = false;
      if (!STATE.saving && !STATE.rerendering) handleCartUpdated();
    }, 800);
  }

  window.__vkInternalCartWrite = false;

  (function patchFetch() {
    if (!window.fetch || window.__vkCheckoutFetchPatched) return;
    window.__vkCheckoutFetchPatched = true;
    var _origFetch = window.fetch;
    window.fetch = function() {
      var args = arguments, url = '';
      try { url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || ''; } catch(_) {}
      var isCartWrite = CART_WRITE_RE.test(url);
      var isInternal = window.__vkInternalCartWrite;
      if (isCartWrite && !isInternal) {
        if (!_detached) {
          var ctrl = document.getElementById('vk-checkout-controller');
          if (ctrl && ctrl.closest('.cart__summary-inner')) {
            ctrl.style.display = 'none'; document.body.appendChild(ctrl);
            STATE.positioned = false; _detached = true;
          }
        }
      }
      return _origFetch.apply(this, args).then(function(res) {
        if (isCartWrite && !isInternal) scheduleRecovery();
        return res;
      });
    };
  })();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('vk:cart:updated', function() { scheduleRecovery(); });
  document.addEventListener('vk:cart-updated', function() { scheduleRecovery(); });
})();
